/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { LocationInstance } from "../../models/location-instance";
import { Point } from "../../models/point";

import { Component, Input, OnDestroy, AfterViewInit, EventEmitter, Output } from '@angular/core';
import * as createjs from 'createjs-module';
import { MathModule } from "../../shared/math.module";

import Tween = createjs.Tween;
import { ImageService } from "app/services/image.service";
import { ZoneInstance } from "app/models/zone-instance";
import { ObjectInstance } from "app/models/object-instance";
import { TagInstance } from "app/models/tag-instance";

declare var $: any;

const DEFAULT_TWEEN_DELAY = 500;
const SCALE_INDEX_MULTIPLIER = .05;
const MIN_SCALE_INDEX = 1;
const SCALE_INDEX_100_PERCENT = 20;
const MAX_SCALE_INDEX = 200;
const ZOOM_ZONE_LEVEL = 1.25;
const VERTICAL_PADDING = 0;

@Component({
  selector: 'app-new-map',
  templateUrl: './new-map.component.html'
})
export class NewMapComponent implements AfterViewInit, OnDestroy {

  /**
   * Output Method - Called when an Instance's Icon (Asset / Person) was clicked
   * on on the Map.
   */
  @Output()
  onInstanceClicked = new EventEmitter<InstanceClickedEvent>();

  /**
   * Output Method - Called when an Tag's Icon was clicked on on the Map.
   */
  @Output()
  onTagClicked = new EventEmitter<TagClickedEvent>();

  tweenDelay = DEFAULT_TWEEN_DELAY;

  /*
   * CreateJS Stage Variables / Components.
   */
  // Canvas Element being drawn
  canvasElement: HTMLCanvasElement;
  // Stage where everything is drawn
  stage: createjs.Stage;
  // createjs.Ticker "tick" event listener, used to update the Stage.
  onTickFunction: Function;

  /*
   * Variables / Components used to draw the Location
   */ 
  /** Location whose image should be drawn on the CreateJS Stage / HTML Canvas  */
  selectedLocation: LocationInstance = null;
  /** CreateJS Image that displayes the Location Image */
  selectedLocationImage: createjs.Bitmap = null;

  /*
   * Variables / Components used to draw the Zones
   */
  // Zones to be drawn on the Location Image (if the Map Options permit)
  zonesToDisplay: ZoneInstance[] = [];
  // Map of the Zones to their CreateJS UI Components
  zonesToDisplaysMap: Map<number, ZoneUis> = new Map();

  /*
   * Variables / Components used to draw the Assets
   */
  // Assets being displayed
  assetsToDisplay: ObjectInstance[] = [];
  // Map of the Assets being displayed to their UI Components
  assetsToDisplaysMap: Map<number, ObjectUis> = new Map();

  /*
   * Variables / Components used to draw the People
   */
  // People being displayed
  peopleToDisplay: ObjectInstance[] = [];
  // Map of the People being displayed to their UI Components
  peopleToDisplaysMap: Map<number, ObjectUis> = new Map();

  /*
   * Variables / Components used to draw the Tag
   */
  // Tags being displayed
  tagsToDisplay: TagInstance[] = [];
  // Map of the Tags being displayed to their UI Components
  tagsToDisplaysMap: Map<number, TagUis> = new Map();

  // Timer used on Window Resize.  Delays the resizing of the Canvas until the User has stopped resizing the Window for half a second.  Done to improve performance/limit the number of times the Canvas is resized.
  canvasResizeTimer = null;

  // Variables used in Zooming
  stageScaleIndex: number = SCALE_INDEX_100_PERCENT;
  initialScale: number;

  // Variables used in Panning
  currentPosition = new Point();

  // Flag used to cancel any click events that occur... Set when performing various operations, reset on each mouse down unless performing an operation (panning/drawing)
  cancelClickEvent = false;

  /**
   * Constructor
   * @param imageService Image Service, needed to get the path for Icons / Images
   */
  constructor(
    private imageService: ImageService  // Needed for getting the Images used for the Location's Floorplan and the Asset / People Icons
  ) { }

  /**
   * Called after the Component is displayed. 
   * Used to setup the CreateJS Stage / HTML5 Canvas elements.
   */
  ngAfterViewInit(): void {
    // Get the Canvas Element used by the Stage where everything is drawn
    this.canvasElement = document.getElementById(this.getMapCanvasId()) as HTMLCanvasElement;

    // Resize the Canvas so that it has a 1:1 ratio between actual pixels and virtual pixels
    this.resizeCanvas();

    // Create the createjs.Stage where everything gets drawn
    this.stage = new createjs.Stage(this.canvasElement);
    this.stage.name = "monitor-zone-map";

    // Attach a Function to execute on each createjs.Ticker "tick", used to redraw the map
    this.onTickFunction = createjs.Ticker.on("tick", this.updateStage.bind(this));
  }

  /**
   * Called when the Component is being destroyed.
   * Used to stop the background process that updates the CreateJS Stage
   */
  ngOnDestroy(): void {
    // Clean up/remove the function attached to the "tick" events of the createjs.Ticker, used to redraw the map... The map is gone!
    createjs.Ticker.off("tick", this.onTickFunction);
  }

  /**
   * Updates the create.js Stage being used to display the Location / Zone
   * and any tracked Assets / People. 
   */
  private updateStage() {
    this.stage.update();
  }

  /**
   * Sets the Location being displayed to the given Location.  If null is passed,
   * then no Location will be shown and the stage / canvas will be cleared. 
   * 
   * This returns a Promise that ends when the Location image is loaded / displayed
   * in the CreateJS Stage. 
   */
  public async setDisplayedLocation(location: LocationInstance): Promise<void> {
    console.log(`Setting the following Display Location to the New Map: '${location?.id}/${location?.name}'...`);
    await this.setLocation(location);
  }

  /**
   * Sets the selected Location being displayed
   */
  private async setLocation(location: LocationInstance) : Promise<void> {
    if (this.selectedLocation != null && location != null && this.selectedLocation?.id == location?.id) {
      console.warn("Ignoring 'location' being set to New Map Component as it is the same as the old Location.");
      return;
    }

    // Perform anything needed to unselect a currently selected Location
    if (this.selectedLocation != null) {
      this.eraseLocation();
    }

    // Update the selected Location to the Location passed in
    this.selectedLocation = location;

    // Perform anything needed to select the currently selected Location
    if (this.selectedLocation != null) {
      await this.drawLocation();
    }
  }

  /**
   * Erases the displayed Location and any Assets / People from
   * the CreateJS Stage. This is usually called when setting the
   * displayed Location to null / unselected. 
   */
  private eraseLocation(): void {
    // Remove any Tags being displayed
    this.tagsToDisplay.forEach(displayedTag => this.removeTagFromDisplay(displayedTag));

    // Remove any Assets being displayed
    this.assetsToDisplay.forEach(displayedAsset => this.removeAssetFromDisplay(displayedAsset));

    // Remove any People being displayed
    this.peopleToDisplay.forEach(displayedPerson => this.removePersonFromDisplay(displayedPerson));

    // Remove any Zones being displayed
    this.zonesToDisplay.forEach(displayedZone => this.removeZoneFromDisplay(displayedZone));

    // Remove anything else left on the CreateJS Stage (When the Location is erased everything should be removed)
    if (this.stage != null) {
      this.stage.removeAllChildren();
    }

    // Set the CreateJS Image being used for the Location's Floorplan to null so it can be recreated
    if (this.selectedLocationImage != null) {
      this.selectedLocationImage = null;
    }
  }

  /**
   * Draws the currently selected Location on the CreateJS Canvas
   */
  private async drawLocation(): Promise<void> {
    // Make sure a Location was selected and that it has an Image...
    if (this.selectedLocation?.image?.id == null) {
      console.warn("Ignoring Draw Location as the Location Image doesn't exist.");
      return;
    }

    // Create the Image for the Location
    const image = document.createElement('img');
    image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
    image.src = this.imageService.getImageSourceForImageId(this.selectedLocation.image.id);
    this.selectedLocationImage = new createjs.Bitmap(image);

    /*
     * Schedule a Function to be kicked off once the Location Image is loaded.
     * 
     * Using the "Deferred()" Function to have this async function await for the Location Image 
     * to be loaded / added to the CreateJS Stage before continuing. This needs to be added 
     * first before drawing the Zones or overlaps / display issues may arise. 
     */
    const locationImageLoadingDeferred = new Deferred();
    this.selectedLocationImage.image.onload = this.onLocationImageLoaded.bind(this, locationImageLoadingDeferred);
    await locationImageLoadingDeferred.promise;
  }

  /**
   * Called when the Location Image (<img> tag) is loaded.  
   * Used to add the Location Image to the CreateJS Stage and then
   * Scale / Center the Image. 
   */
  async onLocationImageLoaded(deferred?: any): Promise<void> {
    // Add the Location Image to the EaselJS Stage to display it
    this.stage.addChildAt(this.selectedLocationImage, 0);

    // Scale the Stage so that the entire Image can be shown in the display
    {
      const horizontalScale = this.canvasElement.offsetWidth / this.selectedLocationImage.image.width;
      const verticalScale = (this.canvasElement.offsetHeight - VERTICAL_PADDING) / this.selectedLocationImage.image.height;
      const scaleIndex = Math.floor((horizontalScale < verticalScale ? horizontalScale : verticalScale) / SCALE_INDEX_MULTIPLIER);
      this.setScaleIndex(scaleIndex, true);
    }

    // Center the Image in the center of the display
    {
      const diffX = this.canvasElement.offsetWidth - (this.stage.scaleX * this.selectedLocationImage.image.width);
      const diffY = this.canvasElement.offsetHeight - VERTICAL_PADDING - (this.stage.scaleY * this.selectedLocationImage.image.height);
      this.stage.x = diffX / 2;
      this.stage.y = diffY / 2;
    }

    // Resolve the given "Deferred" Function (if one was passed) Promise so the "drawLocation()" async function continues processing.
    if (deferred != null) {
      deferred.resolve();
    }
  }

  /**
   * Adds the given Zone to the display so that it is drawn on the Location
   * Image (if Map Options permit).
   */
  public async addZoneToDisplay(zoneInstance: ZoneInstance): Promise<void> {
    // Make sure this is a real Zone...
    if (zoneInstance?.id == null) {
      console.warn("Cancelling adding Zone to Display since the Zone Id could not be obtained.");
      return;
    }
    // Check to see if the Zone is already displayed, if so nothing needs to be done
    if (this.zonesToDisplay.find(zone => zone?.id == zoneInstance?.id) != null) {
      console.warn("Cancelling adding Zone to Display since zone is already displayed!");
      return;
    }

    // Add the Zone
    this.zonesToDisplay.push(zoneInstance);

    /*
     * Draw the Zone
     */
    // Get the Zone UIs currently being used for the Zone Instance (if none exist, create one)
    let zoneUis = this.zonesToDisplaysMap.get(zoneInstance.id);
    if (zoneUis == null) {
      this.zonesToDisplaysMap.set(zoneInstance.id, new ZoneUis());
      zoneUis = this.zonesToDisplaysMap.get(zoneInstance.id);
    }

    /*
     * Calculate the Zone Parameters (if needed).
     * These are needed when performing certain operations, such as drawing the Zone's Name.
     * Still figuring these out for re-factoring if needed.
     */
    if (zoneUis.zoneParameters == null) {
      zoneUis.zoneParameters = this.createZoneParameters(zoneInstance);
    }

    // Draw the Zone's Polgon
    if (zoneUis.zonePolygonShape == null) {
      zoneUis.zonePolygonShape = new createjs.Shape();
      // Set the alpha so that it's slightly see through
      zoneUis.zonePolygonShape.alpha = 0.5;
      // Set the Line Color to the custom one defined for the Zone
      zoneUis.zonePolygonShape.graphics.setStrokeStyle(1).beginStroke("black").beginFill(this.rgbToHex(zoneInstance.color.red, zoneInstance.color.green, zoneInstance.color.blue)); 
      // Draw the outline of the Zone by cycling through the Perimeter Points
      let startX: number = null, startY: number = null;
      zoneInstance.perimeterPoints.forEach(point => {
        const x = this.translatePositionValue(point.x);
        const y = this.translatePositionValue(point.y);
        if (startX == null) {
          zoneUis.zonePolygonShape.graphics.moveTo(x, y);
          startX = x;
          startY = y;
        } else {
          zoneUis.zonePolygonShape.graphics.lineTo(x, y);
        }
      });
      zoneUis.zonePolygonShape.graphics.lineTo(startX, startY);
      zoneUis.zonePolygonShape.graphics.endFill();

      // Add the CreateJS Shape used for the Zone to the Stage on top of the Location Image (which is at index 0)
      this.stage.addChildAt(zoneUis.zonePolygonShape, 1);
    }

    // Draw the Zone's Name 
    if (zoneUis.zoneNameText == null) {
      // Figure some things out for the Zone Name Display
      const fontSize = this.getFontSizeForZoneNames();
      const colorString = this.getTextColorStringForZone(zoneInstance);
      const midPoint = zoneUis.zoneParameters.midPointName;

      console.log(`Initial Scale: '${this.initialScale}', Font Size: '${fontSize}'`)

      // Create the CreateJS Text used to display the Zone Name
      zoneUis.zoneNameText = new createjs.Text(zoneInstance.name, fontSize + "px Arial", colorString);
      zoneUis.zoneNameText.alpha = 1.0;
      zoneUis.zoneNameText.x = this.translatePositionValue(midPoint.x);
      zoneUis.zoneNameText.y = this.translatePositionValue(midPoint.y);
      zoneUis.zoneNameText.textBaseline = "alphabetic";

      // Add the CreateJS Text yused for the Zone Name to the CreateJS Stage
      this.stage.addChildAt(zoneUis.zoneNameText, this.stage.numChildren);
    }
  }

  /**
   * Removes the given Zone from being drawn on the display (if permitted by Map Options)
   */
  public async removeZoneFromDisplay(zoneInstance: ZoneInstance): Promise<void> {
    // Get the Zone Instance being displayed (if it is).  If it is not being displayed, nothing needs to be done.
    const displayedZone = this.zonesToDisplay.find(zone => zone?.id == zoneInstance?.id);
    if (displayedZone == null) {
      return;
    }
    // Remove the Zone from the displayed Zones since it is being displayed
    this.zonesToDisplay = this.zonesToDisplay.filter(z => z?.id != zoneInstance?.id);

    // Get the UIs being used to display the Zone Instance.  If none exist, nothing needs to be done.
    const zoneUis = this.zonesToDisplaysMap.get(displayedZone.id);
    if (zoneUis == null) {
      return;
    }
    // Remove the UIs since they existed
    this.zonesToDisplaysMap.delete(displayedZone.id);

    // Remove the Zone Polygon if it was drawn
    if (zoneUis.zonePolygonShape != null) {
      this.stage.removeChild(zoneUis.zonePolygonShape);
      zoneUis.zonePolygonShape = null;
    }

    // Remove the Zone Name Text if it was drawn
    if (zoneUis.zoneNameText != null) {
      this.stage.removeChild(zoneUis.zoneNameText);
      zoneUis.zoneNameText = null;
    }
  }

  /**
   * Removes all Zones from the Display
   */
  public async removeAllZonesFromDisplay(): Promise<void> {
    if (this.zonesToDisplay.length == 0) {
      return;
    }

    await Promise.all(this.zonesToDisplay.map(zone => this.removeZoneFromDisplay(zone)));
  }





  /**
   * Adds the given Asset to be drawn on the Location's Floorplan Image.
   */
  public async addAssetToDisplay(asset: ObjectInstance): Promise<void> {
    if (asset?.id == null) {
      console.warn("Cancelling adding Asset to display as the Asset ID could not be obtained.");
      return;
    }
    if (this.assetsToDisplay.find(a => a?.id == asset?.id) != null) {
      console.warn("Cancelling adding Asset to display as the Asset is already being displayed!");
      return;
    }
    // Add the Asset
    this.assetsToDisplay.push(asset);

    /*
     * Draw the Asset
     */
    // Get the AssetUis currently being used for the Asset (if none exist, create one)
    let assetUis = this.assetsToDisplaysMap.get(asset.id);
    if (assetUis == null) {
      this.assetsToDisplaysMap.set(asset.id, new ObjectUis());
      assetUis = this.assetsToDisplaysMap.get(asset.id);
    }

    // Draw the Asset's Icon
    if (assetUis.icon == null) {
      // Create the Image for the Asset's Icon
      const image = document.createElement('img');
      image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
      image.src = this.getIconPathForObjectInstance(asset);
      assetUis.icon = new createjs.Bitmap(image);

      /*
       * Schedule a Function to be kicked off once the Asset's Icon Image is loaded.
  
       * Using the "iconImageLoadingDeferred" and the "Deferred()" Function to have this
       * async function await for the Location Image to be loaded / added to the CreateJS
       * Stage before continuing. This needs to be added first before drawing the Zones
       * or overlaps / display issues may arise. 
       */
      const iconImageLoadingDeferred = new Deferred();
      assetUis.icon.image.onload = this.onObjectInstanceIconImageLoaded.bind(this, iconImageLoadingDeferred, asset, assetUis);
      await iconImageLoadingDeferred.promise;
    }
  }

  /**
   * Called when an Object Instance's Icon Image is loaded.  Uses the "deferred" parameter for synchronous handling, 
   * as it contains a promise that will be resolved when this method finishes. 
   */
  async onObjectInstanceIconImageLoaded(deferred?: any, objectInstance?: ObjectInstance, uis?: ObjectUis) {
    // Position/Scale the Icon, then add it to the Stage so it will be drawn
    try {
      const iconScale = this.getIconScale();
      uis.icon.scaleX = iconScale;
      uis.icon.scaleY = iconScale;
      uis.icon.x = this.translateMetersToPixels(objectInstance.position.x) - (uis.icon.image.width * uis.icon.scaleX / 2);
      uis.icon.y = this.translateMetersToPixels(objectInstance.position.y) - (uis.icon.image.height * uis.icon.scaleY / 2);
      this.stage.addChild(uis.icon);
    } catch (error) {
      console.error("Problem adding Icon to Stage.", error);
    }
    // Add an On Click Handler so that click events can be detected / handled if needed
    uis.icon.on('click', this.onInstanceIconClicked.bind(this, objectInstance, uis));

    // Resolve the passed in deferred Function (if one exists)
    if (deferred != null) {
      deferred.resolve();
    }
  }

  /**
   * Called when the given Instance's Icon is clicked. 
   * Emits an InstanceClickedEvent Event to all the handlers.
   */
  onInstanceIconClicked(instance: ObjectInstance, uis: ObjectUis, event: createjs.MouseEvent): void {
    this.onInstanceClicked.emit({ instance: instance, event: event} as InstanceClickedEvent);
  }

  /**
   * Updates the Position of the given Asset on the display
   */
  public async updateAssetPosition(asset: ObjectInstance): Promise<void> {
    // Get the UI Components for the given Asset
    const assetUis = this.assetsToDisplaysMap.get(asset?.id);
    if (assetUis == null || assetUis.icon == null) {
      console.warn("Ignoring update to Asset Position as the Asset isn't displayed.");
      return;
    }

    // Update the Icon's position based on the Asset's current position
    const iconScale = this.getIconScale();
    assetUis.icon.scaleX = iconScale;
    assetUis.icon.scaleY = iconScale;
    const x = this.translateMetersToPixels(asset.position.x) - (assetUis.icon.image.width * iconScale / 2);
    const y = this.translateMetersToPixels(asset.position.y) - (assetUis.icon.image.height * iconScale / 2);
    createjs.Tween.get(assetUis.icon, {override: true}).to({x: x, y: y}, this.tweenDelay);
  }

  /**
   * Returns a boolean indicating if the given Asset is currently being drawn
   * on the display.
   */
  public isAssetDisplayed(asset: ObjectInstance): boolean {
    return this.assetsToDisplaysMap.has(asset?.id);
  }

  /**
   * Removes the given Asset from the display
   */
  public async removeAssetFromDisplay(asset: ObjectInstance): Promise<void> {
    // Get the Asset being displayed (if it is).  If it is not being displayed, nothing needs to be done.
    const displayedAsset = this.assetsToDisplay.find(a => a?.id == asset?.id);
    if (displayedAsset == null) {
      return;
    }
    // Remove the Zone from the displayed Zones since it is being displayed
    this.assetsToDisplay = this.assetsToDisplay.filter(a => a?.id != asset?.id);
    
    // Get the UIs being used to display the Asset.  If none exist, nothing needs to be done.
    const assetUis = this.assetsToDisplaysMap.get(displayedAsset.id);
    if (assetUis == null) {
      return;
    }
    // Remove the UIs since they existed
    this.assetsToDisplaysMap.delete(displayedAsset.id);

    // Remove the Asset's Icon if it was drawn
    if (assetUis.icon != null) {
      this.stage.removeChild(assetUis.icon);
      assetUis.icon = null;
    }
  }

  /**
   * Removes all Asset's from the display
   */
  public async removeAllAssetsFromDisplay(): Promise<void> {
    if (this.assetsToDisplay?.length == 0) {
      return;
    }

    await Promise.all(this.assetsToDisplay.map(asset => this.removeAssetFromDisplay(asset)));
  }





  /**
   * Adds the given Person to be drawn on the Location's Floorplan Image.
   */
  public async addPersonToDisplay(person: ObjectInstance): Promise<void> {
    if (person?.id == null) {
      console.warn("Cancelling adding Person to display as the Asset ID could not be obtained.");
      return;
    }
    if (this.peopleToDisplay.find(p => p?.id == person?.id) != null) {
      console.warn("Cancelling adding Person to display as the Person is already being displayed!");
      return;
    }
    // Add the Person
    this.peopleToDisplay.push(person);

    /*
     * Draw the Person
     */
    // Get the ObjectUis currently being used for the Asset (if none exist, create one)
    let personUis = this.peopleToDisplaysMap.get(person.id);
    if (personUis == null) {
      this.peopleToDisplaysMap.set(person.id, new ObjectUis());
      personUis = this.peopleToDisplaysMap.get(person.id);
    }

    // Draw the Person's Icon
    if (personUis.icon == null) {
      // Create the Image for the Person's Icon
      const image = document.createElement('img');
      image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
      image.src = this.getIconPathForObjectInstance(person);
      personUis.icon = new createjs.Bitmap(image);

      /*
       * Schedule a Function to be kicked off once the Person's Icon Image is loaded.
  
       * Using the "iconImageLoadingDeferred" and the "Deferred()" Function to have this
       * async function await for the Location Image to be loaded / added to the CreateJS
       * Stage before continuing. This needs to be added first before drawing the Zones
       * or overlaps / display issues may arise. 
       */
      const iconImageLoadingDeferred = new Deferred();
      personUis.icon.image.onload = this.onObjectInstanceIconImageLoaded.bind(this, iconImageLoadingDeferred, person, personUis);
      await iconImageLoadingDeferred.promise;
    }
  }

  /**
   * Updates the Position of the given Person on the display
   */
  public async updatePersonPosition(person: ObjectInstance): Promise<void> {
    // Get the UI Components for the given Person
    const personUis = this.peopleToDisplaysMap.get(person?.id);
    if (personUis == null || personUis.icon == null) {
      console.warn("Ignoring update to Person Position as the Person isn't displayed.");
      return;
    }

    // Update the Icon's position based on the Person's current position
    const iconScale = this.getIconScale();
    personUis.icon.scaleX = iconScale;
    personUis.icon.scaleY = iconScale;
    const x = this.translateMetersToPixels(person.position.x) - (personUis.icon.image.width * iconScale / 2);
    const y = this.translateMetersToPixels(person.position.y) - (personUis.icon.image.height * iconScale / 2);
    createjs.Tween.get(personUis.icon, {override: true}).to({x: x, y: y}, this.tweenDelay);
  }

  /**
   * Removes the given Person from the display
   */
  public async removePersonFromDisplay(person: ObjectInstance): Promise<void> {
    // Get the Person being displayed (if it is).  If it is not being displayed, nothing needs to be done.
    const displayedPerson = this.peopleToDisplay.find(p => p?.id == person?.id);
    if (displayedPerson == null) {
      return;
    }
    // Remove the Person from the displayed People since it is being removed
    this.peopleToDisplay = this.peopleToDisplay.filter(a => a?.id != person?.id);
    
    // Get the UIs being used to display the Person.  If none exist, nothing needs to be done.
    const personUis = this.peopleToDisplaysMap.get(displayedPerson.id);
    if (personUis == null) {
      return;
    }
    // Remove the UIs since they existed
    this.peopleToDisplaysMap.delete(displayedPerson.id);

    // Remove the Person's Icon if it was drawn
    if (personUis.icon != null) {
      this.stage.removeChild(personUis.icon);
      personUis.icon = null;
    }
  }

  /**
   * Removes all People from the display
   */
  public async removeAllPeopleFromDisplay(): Promise<void> {
    if (this.peopleToDisplay?.length == 0) {
      return;
    }
    
    await Promise.all(this.peopleToDisplay.map(person => this.removePersonFromDisplay(person)));
  }






  /**
   * Adds the given Tag to be drawn on the Location's Floorplan Image.
   */
  public async addTagToDisplay(tag: TagInstance): Promise<void> {
    if (tag?.id == null) {
      console.warn("Cancelling adding Tag to display as the Tag ID could not be obtained.");
      return;
    }
    if (this.tagsToDisplay.find(t => t?.id == tag?.id) != null) {
      console.warn("Cancelling adding Tag to display as the Tag is already being displayed!");
      return;
    }
    // Add the Tag
    this.tagsToDisplay.push(tag);

    /*
     * Draw the Tag
     */
    // Get the TagUis currently being used for the Tag (if none exist, create one)
    let tagUis = this.tagsToDisplaysMap.get(tag.id);
    if (tagUis == null) {
      this.tagsToDisplaysMap.set(tag.id, new TagUis());
      tagUis = this.tagsToDisplaysMap.get(tag.id);
    }

    // Draw the Tag's Icon
    if (tagUis.icon == null) {
      // Create the Image for the Tag's Icon
      const image = document.createElement('img');
      image.crossOrigin = 'Anyonymous'; //Enable Cross Origin (Need to do it this way due to not being able to inside EaselJS itself - https://stackoverflow.com/questions/41468523/easeljs-cross-domain-images-workaround)
      image.src = this.getIconPathForTag(tag);
      tagUis.icon = new createjs.Bitmap(image);

      /*
       * Schedule a Function to be kicked off once the Tag's Icon Image is loaded.
  
       * Using the "iconImageLoadingDeferred" and the "Deferred()" Function to have this
       * async function await for the Tag Image to be loaded / added to the CreateJS
       * Stage before continuing.  
       */
      const iconImageLoadingDeferred = new Deferred();
      tagUis.icon.image.onload = this.onTagIconImageLoaded.bind(this, iconImageLoadingDeferred, tag, tagUis);
      await iconImageLoadingDeferred.promise;
    }
  }

  /**
   * Called when an Tag's Icon Image is loaded.  Uses the "deferred" parameter for synchronous handling, 
   * as it contains a promise that will be resolved when this method finishes. 
   */
  async onTagIconImageLoaded(deferred?: any, tag?: TagInstance, uis?: TagUis) {
    // Position/Scale the Icon, then add it to the Stage so it will be drawn
    try {
      const iconScale = this.getIconScale();
      uis.icon.scaleX = iconScale;
      uis.icon.scaleY = iconScale;
      uis.icon.x = this.translateMetersToPixels(tag.position.x) - (uis.icon.image.width * uis.icon.scaleX / 2);
      uis.icon.y = this.translateMetersToPixels(tag.position.y) - (uis.icon.image.height * uis.icon.scaleY / 2);
      this.stage.addChild(uis.icon);
    } catch (error) {
      console.error("Problem adding Icon to Stage.", error);
    }
    // Add an On Click Handler so that click events can be detected / handled if needed
    uis.icon.on('click', this.onTagIconClicked.bind(this, tag, uis));

    // Resolve the passed in deferred Function (if one exists)
    if (deferred != null) {
      deferred.resolve();
    }
  }

  /**
   * Called when the given Tag's Icon is clicked. 
   * Emits an TagClickedEvent Event to all the handlers.
   */
  onTagIconClicked(tag: TagInstance, uis: TagUis, event: createjs.MouseEvent): void {
    this.onTagClicked.emit({ tag: tag, event: event} as TagClickedEvent);
  }

  /**
   * Updates the Position of the given Tag on the display
   */
  public async updateTagPosition(tag: TagInstance): Promise<void> {
    // Get the UI Components for the given Tag
    const tagUis = this.tagsToDisplaysMap.get(tag?.id);
    if (tagUis == null || tagUis.icon == null) {
      console.warn("Ignoring update to Tag Position as the Tag isn't displayed.");
      return;
    }

    // Update the Icon's position based on the Tag's current position
    const iconScale = this.getIconScale();
    tagUis.icon.scaleX = iconScale;
    tagUis.icon.scaleY = iconScale;
    const x = this.translateMetersToPixels(tag.position.x) - (tagUis.icon.image.width * iconScale / 2);
    const y = this.translateMetersToPixels(tag.position.y) - (tagUis.icon.image.height * iconScale / 2);
    createjs.Tween.get(tagUis.icon, {override: true}).to({x: x, y: y}, this.tweenDelay);
  }

  /**
   * Returns a boolean indicating if the given Tag is currently being drawn
   * on the display.
   */
  public isTagDisplayed(tag: TagInstance): boolean {
    return this.tagsToDisplaysMap.has(tag?.id);
  }

  /**
   * Removes the given Tag from the display
   */
  public async removeTagFromDisplay(tag: TagInstance): Promise<void> {
    // Get the Tag being displayed (if it is).  If it is not being displayed, nothing needs to be done.
    const displayedTag = this.tagsToDisplay.find(t => t?.id == tag?.id);
    if (displayedTag == null) {
      return;
    }
    // Remove the Tag from the displayed Tags since it is being displayed
    this.tagsToDisplay = this.tagsToDisplay.filter(t => t?.id != tag?.id);
    
    // Get the UIs being used to display the Tag.  If none exist, nothing needs to be done.
    const tagUis = this.tagsToDisplaysMap.get(displayedTag.id);
    if (tagUis == null) {
      return;
    }
    // Remove the UIs since they existed
    this.tagsToDisplaysMap.delete(displayedTag.id);

    // Remove the Tag's Icon if it was drawn
    if (tagUis.icon != null) {
      this.stage.removeChild(tagUis.icon);
      tagUis.icon = null;
    }
  }

  /**
   * Removes all Tag's from the display
   */
  public async removeAllTagsFromDisplay(): Promise<void> {
    if (this.tagsToDisplay?.length == 0) {
      return;
    }

    await Promise.all(this.tagsToDisplay.map(tag => this.removeTagFromDisplay(tag)));
  }





  /**
   * Returns the Font Size to use for the Zone Names.
   * This is based on the initialScale applied to the Floorplan image.
   * This logic was copied from map.component.ts.
   */
  private getFontSizeForZoneNames(): number {
    if (this.initialScale > 2.5) {
      return 6
    } else if (this.initialScale > .74) {
      return 16;
    } else if (this.initialScale > .6) {
      return 24;
    } else if (this.initialScale > .5) {
      return 36;
    } else {
      return 48;
    }
  }

  /**
   * Returns the color string to use for the Zone Names being drawn
   * for the given Zone. 
   */
  private getTextColorStringForZone(zoneInstance: ZoneInstance): string {
    // If the Zone has a "Text Color" set, just use that...
    if (zoneInstance.textColor != null) {
      return this.rgbToHex(zoneInstance.textColor.red, zoneInstance.textColor.green, zoneInstance.textColor.blue);
    }

    /*
     * Zone doesn't have a "Text Color" set, based it off the Zone's background color.
     * Here we try to lighten / darken the background color based on the background color
     * to provide a better contrast...
     */
    const luma = (0.2126 * zoneInstance.color.red) + (0.7152 * zoneInstance.color.green) + (0.0722 * zoneInstance.color.blue);
    if (luma < 40) {
      // If the color is darker than normal, the text will be lighter
      return this.rgbToHex(Math.floor(zoneInstance.color.red + (.75 * (255 - zoneInstance.color.red))),
        Math.floor(zoneInstance.color.green + (.75 * (255 - zoneInstance.color.green))),
        Math.floor(zoneInstance.color.blue + (.75 * (255 - zoneInstance.color.blue))));
    } else {
      // If the color is lighter than normal, the text will be darker
      return this.rgbToHex(Math.floor(.25 * zoneInstance.color.red),
        Math.floor(.25 * zoneInstance.color.green),
        Math.floor(.25 * zoneInstance.color.blue));
    }
  }

  /*
   * Set the Canvas's Height/Width attributes, to that of the pixel height/width.
   * This keeps the Canvas at the same scale as the rest of the page since style width/height
   * are 100% to use up all available space.
   * This gets called on the initial display, and it is tied to any window resizing to fix the scaling
   * in case the size changes.
   */
  private resizeCanvas(): void {
    this.canvasElement.height = this.canvasElement.clientHeight;
    this.canvasElement.width = this.canvasElement.clientWidth;
  }

  /**
   * Called on Window Resize.  This schedules a function call to 
   * resize the canvas with a half second debounce for performance. 
   */
  onWindowResize(): void {
    if (this.canvasResizeTimer != null) {
      clearTimeout(this.canvasResizeTimer);
    }
    this.canvasResizeTimer = setTimeout(this.resizeCanvas.bind(this), 500);
  }

  moveStage(pixelsX: number, pixelsY: number): void {
    this.stage.x = this.stage.x + pixelsX;
    this.stage.y = this.stage.y + pixelsY;
  }

  translatePositionValue(posVal: number): number {
    if (this.selectedLocation?.scale == null) {
      return posVal;
    } else {
      return posVal * this.selectedLocation.scale;
    }
  }

  rgbToHex(r: number, g: number, b: number):string {
    if (r > 255) { r = 255; }
    if (g > 255) { g = 255; }
    if (b > 255) { b = 255; }
    return "#" + this.colorToHex(r) + this.colorToHex(g) + this.colorToHex(b);
  }

  colorToHex(c: number): string {
    const h = c.toString(16);
    return h.length == 1? "0" + h : h;
  }

  /**
   * Returns the URL to use for the given Object Instance's Icon Image.
   * This is needed to display the Asset's / People's Icon on the Floorplan.
   */
  private getIconPathForObjectInstance(objectInstance: ObjectInstance): string {
    return this.imageService.getIconSourceForObjectInstance(objectInstance);
  }

  /**
   * Returns the URL to use for the given Tag's Icon Image.
   * This is needed to display the Tag's Icon on the Floorplan.
   */
  private getIconPathForTag(tag: TagInstance): string {
    return this.imageService.getIconSourceForTag(tag);
  }

  /**
   * Returns the Point that is at the center of the Location's Image.
   * From what I can tell, this is only used when doing the "zoomIn(number)" function
   * for some reason.
   */
  getCenterPointOfCanvas(): Point {
    const point = new Point();

    // Find out the size of the image in the current scaling
    const currentScale = this.stageScaleIndex * SCALE_INDEX_MULTIPLIER;
    const imageWidth = this.selectedLocationImage.image.width;
    const imageHeight = this.selectedLocationImage.image.height;
    const imageWidthCurrent = imageWidth * currentScale;
    const imageHeightCurrent = imageHeight * currentScale;

    // Find pixel midpoint of canvas
    const pixelsMidpointX = (this.canvasElement.width / 2) - (imageWidthCurrent / 2) + this.stage.x;
    const pixelsMidpointY = (this.canvasElement.height / 2) - (imageHeightCurrent / 2) + this.stage.y;

    point.x = pixelsMidpointX;
    point.y = pixelsMidpointY;

    return point;
  }

  /**
   * Returns the page to it's original zooming
   */
  zoomToOriginal(): void {
    if (this.selectedLocation == null || this.selectedLocationImage == null) {
      console.warn("Ignoring 'Zoom to Original' call as no Location is being displayed...");
      return;
    }

    // TODO: Animate this

    // Set the scale back to original scaling
    const horizontalScale = this.canvasElement.offsetWidth / this.selectedLocationImage.image.width;
    const verticalScale = (this.canvasElement.offsetHeight - VERTICAL_PADDING) / this.selectedLocationImage.image.height;
    const scaleIndex = Math.floor((horizontalScale < verticalScale ? horizontalScale : verticalScale) / SCALE_INDEX_MULTIPLIER);
    this.setScaleIndex(scaleIndex);

    // Move the coordinates back to centered
    const diffX = this.canvasElement.offsetWidth - (this.stage.scaleX * this.selectedLocationImage.image.width);
    const diffY = this.canvasElement.offsetHeight - VERTICAL_PADDING - (this.stage.scaleY * this.selectedLocationImage.image.height);
    this.stage.x = diffX / 2;
    this.stage.y = diffY / 2;
  }

  /**
   * Includes setting scale and moving canvas so we zoom on centered pixel
   */
  zoomIn(amount?: number): void {
    console.log("zoomIn(" + amount + ")");
    // Validate / Correct the "amount" parameter
    amount = (!amount) ? 1 : Math.ceil(amount);

    // Get the new Scale Index by adding the amount to the current Scale Index keeping the value between the min / max. 
    const scaleIndexAfterZoom = MathModule.clamp(this.stageScaleIndex + amount, MIN_SCALE_INDEX, MAX_SCALE_INDEX);

    // If the Scale Index is not changing, then no need to do anything else...
    if (scaleIndexAfterZoom == this.stageScaleIndex) {
      console.warn("No longer zooming in as the scale is not changing...");
      return;
    }

    // Get the center point of the canvase before zooming in...  Used to recenter the point after zooming in.
    const centerPoint = this.getCenterPointOfCanvas();

    // Scale the canvas...
    this.setScaleIndex(scaleIndexAfterZoom);

    // Get the center point of the canvase after zooming in...  Used to recenter the point after zooming in.
    const centerPoint2 = this.getCenterPointOfCanvas();

    /*
     * Recenter the page by move the difference between the center point 
     * after the zoom in process. 
     */
    const moveX = centerPoint2.x - centerPoint.x;
    const moveY = centerPoint2.y - centerPoint.y;
    this.moveStage(moveX, moveY);
  }

  /**
   * zoomOut includes setting scale and moving canvas so we zoom on center
   */
  zoomOut(amount?: number): void {
    console.log("zoomOut(" + amount + ")");
    // Validate / Correct the "amount" parameter
    amount = (!amount) ? 1 : Math.ceil(amount);

    // Get the new Scale Index by adding the amount to the current Scale Index keeping the value between the min / max. 
    const scaleIndexAfterZoom = MathModule.clamp(this.stageScaleIndex - amount, MIN_SCALE_INDEX, MAX_SCALE_INDEX);
    if (scaleIndexAfterZoom == this.stageScaleIndex) {
      console.log("Reached maximum zoom out.");
      return;
    }
    
    // Get a point on the canvas with the original scaling applied (not really sure what this point is... but also not sure if it matters. Used to recenter the Image after zooming) 
    const originalX = this.stage.x + this.selectedLocationImage.image.width * this.stageScaleIndex * SCALE_INDEX_MULTIPLIER / 2;
    const originalY = this.stage.y + this.selectedLocationImage.image.height * this.stageScaleIndex * SCALE_INDEX_MULTIPLIER / 2;

    // Get the same point on the canvas with the new scaling applied (not really sure what this point is... but also not sure if it matters. Used to recenter the Image after zooming)
    const newX = this.stage.x + this.selectedLocationImage.image.width * (scaleIndexAfterZoom) * SCALE_INDEX_MULTIPLIER / 2;
    const newY = this.stage.y + this.selectedLocationImage.image.height * (scaleIndexAfterZoom) * SCALE_INDEX_MULTIPLIER / 2;

    // Get the difference between the two points (Used to recenter the Image after zooming)
    const diffX = newX - originalX;
    const diffY = newY - originalY;

    // Scale the canvas...
    this.setScaleIndex(scaleIndexAfterZoom);

    // Recenter the image by moving the difference between the before / after points
    this.moveStage(-diffX, -diffY);
  }

  /**
   * Scales the Location Image (Zooms In / Out depending on the given index)
   * @param scaleIndex - Scale Index that should be applied to the Location Image
   * @param setInitial - Whether we are setting the initial scale on the Location Image or not. 
   *                     Used mainly to set the stored "initialScale" variable for resetting the zoom level. (I think...)
   */
  setScaleIndex(scaleIndex: number, setInitial?: boolean): void {
    // Validate / Correct the "scaleIndex" parameter
    scaleIndex = MathModule.clamp(scaleIndex, MIN_SCALE_INDEX, MAX_SCALE_INDEX);

    // If the scaling index is not changing, no need to do anything.
    if (this.stageScaleIndex == scaleIndex) {
      return;
    }

    // Update the Scale Index to the new value
    console.log("Changing Scale Index: " + this.stageScaleIndex + " => " + scaleIndex);
    this.stageScaleIndex = scaleIndex;

    /*
     * Calculate the new scaling factore to apply to the EaselJS Stage.

     * Each "Scaling Index" = 5%. With 1 being the min index and 200 being 
     * the max index, that allows for scaling the Location Image between 5% 
     * and 1000% it's original size. 
     */ 
    const newScale = scaleIndex * SCALE_INDEX_MULTIPLIER;
    this.stage.scaleX = newScale;
    this.stage.scaleY = newScale;

    // If setting the initial scale, store the value for the "reset display" functionality. (Reset the Image to it's original size / scale)
    if (setInitial) {
      this.initialScale = newScale;
    }
  }

  private getIconScale(): number {
    return (1 / this.stage.scaleX);
  }

  private translateMetersToPixels(posVal: number): number {
    if (this.selectedLocation?.scale == null) {
      return posVal;
    } else {
      return posVal * this.selectedLocation.scale;
    }
  }

  private translatePixelsToMeters(pixelVal: number): number {
    if (this.selectedLocation?.scale == null) {
      return pixelVal;
    } else {
      return pixelVal / this.selectedLocation.scale;
    } 
  }

  /**
   * Rerturns the name of the id being used for the HTML5 Canvas
   * element that will be used for the CreateJS Stage. 
   */
  getMapCanvasId(): string {
    return "newMapCanvas";
  }

  /**
   * Executes the "Zoom to Zone" Functionality for the given Zone Instance.
   * It is assumed that this Zone Instance is currently part of the Zones
   * being displayed, if not nothing will happen.
   */
  public async executeZoomToZone(zoneInstance: ZoneInstance): Promise<void> {
    console.log(`Executing 'Zoom to Zone' for Zone: '${zoneInstance?.id}/${zoneInstance?.name}'...`);

    // Get the ZoneUis tied to the Zone Instance
    const zoneUis = this.zonesToDisplaysMap.get(zoneInstance?.id);
    if (zoneUis == null) {
      console.warn("Cannot execute 'Zoom to Zone' as ZoneUis were not found for the given Zone.");
      return;
    }

    // Get the ZoneParameters for the Zone Instance
    const zoneParams = zoneUis.zoneParameters;
    if (zoneParams == null) {
      console.warn("Cannot execute 'Zoom to Zone' as ZoneParameters were not found for the given Zone.");
      return;
    }



    const currentScale = this.stageScaleIndex * SCALE_INDEX_MULTIPLIER;

    const zoneMidpointPixelsX = zoneParams.midPointPixels.x * currentScale;
    const zoneMidpointPixelsY = zoneParams.midPointPixels.y * currentScale;

    // ***********************************************
    // Calculate imageWidthCurrent and imageHeightCurrent
    // ***********************************************
    // First calculate the totalWidth and totalHeight in the current scale step
    const imageWidth = this.selectedLocationImage.image.width;
    const imageHeight = this.selectedLocationImage.image.height;

    const imageWidthCurrent = imageWidth * currentScale;
    const imageHeightCurrent = imageHeight * currentScale;

    const currentReduction = this.reduceImageWidthAndHeightByCanvas(imageWidthCurrent, imageHeightCurrent);
    const imageWidthCurrentDisplayed = currentReduction.x;
    const imageHeightCurrentDisplayed = currentReduction.y;
    
    // Find pixel midpoint of canvas
    const pixelsMidpointX = (this.canvasElement.width / 2) - this.stage.x;
    const pixelsMidpointY = (this.canvasElement.height / 2) - this.stage.y;

    const diffMidpointX = pixelsMidpointX - zoneMidpointPixelsX;
    const diffMidpointY = pixelsMidpointY - zoneMidpointPixelsY;

    // Figure out how much scaling will be needed to get this zone on 50% of the screen in width
    let zoneWidthPixels = this.translateMetersToPixels(zoneParams.width) * currentScale;
    let zoneHeightPixels = this.translateMetersToPixels(zoneParams.height) * currentScale;

    let scaleIncrement: number;
    scaleIncrement = 0;
    if (zoneWidthPixels * ZOOM_ZONE_LEVEL < imageWidthCurrentDisplayed && zoneHeightPixels * ZOOM_ZONE_LEVEL < imageHeightCurrentDisplayed) {
      // Zoom In
      while (zoneWidthPixels * ZOOM_ZONE_LEVEL < this.canvasElement.width && zoneHeightPixels * ZOOM_ZONE_LEVEL < this.canvasElement.height) {
        scaleIncrement++;
        const scaleMultiplier = (this.stageScaleIndex + scaleIncrement) * SCALE_INDEX_MULTIPLIER;
        zoneWidthPixels = this.translateMetersToPixels(zoneParams.width) * scaleMultiplier;
        zoneHeightPixels = this.translateMetersToPixels(zoneParams.height) * scaleMultiplier;
      }
    } else if (zoneWidthPixels * ZOOM_ZONE_LEVEL > this.canvasElement.width  || zoneHeightPixels * ZOOM_ZONE_LEVEL > this.canvasElement.height) {
      // Zoom Out
      while (zoneWidthPixels * ZOOM_ZONE_LEVEL > this.canvasElement.width || zoneHeightPixels * ZOOM_ZONE_LEVEL > this.canvasElement.height) {
        scaleIncrement--;
        const scaleMultiplier = (this.stageScaleIndex + scaleIncrement) * SCALE_INDEX_MULTIPLIER;
        zoneWidthPixels = this.translateMetersToPixels(zoneParams.width) * scaleMultiplier;
        zoneHeightPixels = this.translateMetersToPixels(zoneParams.height) * scaleMultiplier;
      }
      scaleIncrement++;
    }

    // Calculate what the new final Scale Index will be after zooming...
    const newFinalScale = this.stageScaleIndex + scaleIncrement;
    
    /*
     * Call the 1st "Tween" method used in the "Zoom to Zone" Process.
     * This one pans the Floorplan so that the Image is centered over 
     * the Zone being zoomed to. Uses the "Deferred" method to have the 
     * thread wait until that 2nd Tween method completes before finishing. 
     */
    const tween1Deferred = new Deferred();
    createjs.Tween.get(this.stage).to({ 
      x: this.stage.x + diffMidpointX, 
      y: this.stage.y + diffMidpointY
      }, this.tweenDelay).call(this.executeZoomToZone_Tween1.bind(this, newFinalScale, zoneParams, tween1Deferred));
    await tween1Deferred.promise;
  }

  /**
   * 1st "Tween" method used in the "Zoom to Zone" process.
   * This one moves the Floorplan Image to center the Zone on the canvas, then calls
   * the 2nd "Tween" method to zoom in / out as needed.  This will wait until the 2nd
   * "Tween" method finishes before returning. 
   */
  private async executeZoomToZone_Tween1(newFinalScale: number, zoneParams: ZoneParameters, deferred?: any): Promise<void> {    
    // Calculate the zoomed parameters
    const zoomedScale = newFinalScale * SCALE_INDEX_MULTIPLIER;

    // Find pixel midpoint of canvas
    const zoneMidpointPixelsZoomedX = zoneParams.midPointPixels.x * zoomedScale;
    const zoneMidpointPixelsZoomedY = zoneParams.midPointPixels.y * zoomedScale;

    const pixelsMidpointZoomedX = (this.canvasElement.width / 2) - this.stage.x;
    const pixelsMidpointZoomedY = (this.canvasElement.height / 2) - this.stage.y;

    const diffMidpointZoomedX = pixelsMidpointZoomedX - zoneMidpointPixelsZoomedX;
    const diffMidpointZoomedY = pixelsMidpointZoomedY - zoneMidpointPixelsZoomedY;

    const xMoveTo = this.stage.x + diffMidpointZoomedX;
    const yMoveTo = this.stage.y + diffMidpointZoomedY;

    /*
     * Call the 2nd "Tween" method used in the "Zoom to Zone" Process.
     * This one zooms in / out as needed.  Uses the "Deferred" method 
     * to have the thread wait until that 2nd Tween method completes 
     * before finishing. 
     */
    const tween2Deferred = new Deferred();
    createjs.Tween.get(this.stage).to({
      x: xMoveTo, 
      y: yMoveTo,
      scaleX: zoomedScale,
      scaleY: zoomedScale
      }, this.tweenDelay).call(this.executeZoomToZone_Tween2.bind(this, newFinalScale, tween2Deferred));
    await tween2Deferred.promise;

    // Resolve the Deferred that may have been passed in to continue that async process if needed
    if (deferred != null) {
      deferred.resolve();
    }
  }

  /**
   * 2nd "Tween" method executed in the "Zoom to Zone" process.
   * This one just updates the Scale Index with the new value. 
   */
  private async executeZoomToZone_Tween2(newFinalScale: number, deferred?: any): Promise<void> {
    //that.stageScaleIndex = that.stageScaleIndex + scaleIncrement;
    this.stageScaleIndex = newFinalScale;

    // Resolve the Deferred that may have been passed in to continue that async process if needed
    if (deferred != null) {
      deferred.resolve();
    }
  }

  /**
   * Called by the "Zoom to Zone" process.  Not really sure what it's doing atm.
   */
  private reduceImageWidthAndHeightByCanvas(imageWidth: number, imageHeight: number): Point {
    const rv = new Point();
    rv.x = this.canvasElement.width;
    rv.y = this.canvasElement.height;

    // If the width or height are larger than the canvas, reduce the width or height to the canvas size
    if (imageWidth > this.canvasElement.width) {
      rv.x = this.canvasElement.width;
    }
    if (imageHeight > this.canvasElement.height) {
      rv.y = this.canvasElement.height;
    }

    // If the canvas is moved over (there is whitespace left or top), recalculate the width and height displayed
    if (imageWidth + this.stage.x > this.canvasElement.width) {
      rv.x = this.canvasElement.width - this.stage.x;
    }
    if (imageHeight + this.stage.y > this.canvasElement.height) {
      rv.y = this.canvasElement.height - this.stage.y;
    }

    return rv;
  }

  /**
   * Figures some things out about the given Zone Instance
   * and creates a ZoneParameters instance with that information.
   * 
   * This was being done in the old map.component.ts and some of this
   * is needed when doing the "Zoom to Zone" or "Drawing the Zone Name"
   * functionality.  
   * 
   * TODO: This still needs to be looked into for cleaning up / refactoring.
   */
  private createZoneParameters(zoneInstance: ZoneInstance): ZoneParameters {
    const rv = new ZoneParameters();

    const midPoint = new Point();
    const midPointName = new Point();

    // Find the coordinates of outside perimeterPoints
    let leftX = 10000;
    let rightX = 0;
    let topY = 10000;
    let bottomY = 0;

    for(const perimeterPoint of zoneInstance.perimeterPoints) {
      if (perimeterPoint.x < leftX) {
        leftX = perimeterPoint.x;
      }
      if (perimeterPoint.x > rightX) {
        rightX = perimeterPoint.x;
      }
      if (perimeterPoint.y < topY) {
        topY = perimeterPoint.y;
      }
      if (perimeterPoint.y > bottomY) {
        bottomY = perimeterPoint.y;
      }
    }

    midPoint.x = leftX + ((rightX - leftX) / 2);
    midPoint.y = topY + ((bottomY - topY) / 2);

    midPointName.x = leftX + ((rightX - leftX) / 4);
    midPointName.y = topY + ((bottomY - topY) / 2);

    rv.midPoint = midPoint;
    rv.midPointPixels = new Point();

    rv.midPointPixels.x = this.translateMetersToPixels(rv.midPoint.x);
    rv.midPointPixels.y = this.translateMetersToPixels(rv.midPoint.y);

    rv.midPointName = midPointName;
    rv.width = rightX - leftX;
    rv.height = bottomY - topY;

    return rv;
  }

}

/**
 * UI Elements for Zone Instances
 */
class ZoneUis {
  // Zone Instance being displayed
  zoneInstance: ZoneInstance;

  // CreateJS Shape used to display the Zone Polygon on the Location Image
  zonePolygonShape: createjs.Shape;

  // CreateJS Text used to display the Zone Name on the Location Image
  zoneNameText: createjs.Text;

  // Zone Parameters, hold some information about the Zone being drawn / displayed
  zoneParameters: ZoneParameters;
}

/**
 * UI Elements for Object Instances.
 */
class ObjectUis {
  // Object Instance being displayed
  objectInstance: ObjectInstance;

  //CreateJS Bitmap used for the Icon on the Map
  icon: createjs.Bitmap;
}

/**
 * UI Elements for Object Instances.
 */
class TagUis {
  // Tag being displayed
  tag: TagInstance;

  //CreateJS Bitmap used for the Icon on the Map
  icon: createjs.Bitmap;
}

/**
 * Zone Parameter Data elements.
 * Pulled this out of map.component.ts, need to look into exactly why all this is needed.
 */
class ZoneParameters {
  midPoint: Point;
  midPointPixels: Point;
  midPointName: Point;
  width: number;
  widthPixels: number;
  height: number;
  heightPixels: number;
}

/**
 * Taken from here:
 * https://stackoverflow.com/questions/26150232/resolve-javascript-promise-outside-the-promise-constructor-scope
 *
 * Allows for creating a "lock" to ensure multiple threads / processes can be 
 * executed in order using async / await.  
 * 
 * This is currently used to ensure the Location Image is loaded before returning 
 * from the "drawLocation()" method.  
 */
function Deferred() {
  var self = this;
  this.promise = new Promise(function(resolve, reject) {
    self.reject = reject
    self.resolve = resolve
  });
}

/**
 * Event Structure used when the drawn Icon's representing Instances
 * are clicked on in the Map.  This gets fired off to the any handler.
 */
export interface InstanceClickedEvent {
  instance: ObjectInstance;
  event: createjs.MouseEvent;
}

/**
 * Event Structure used when the drawn Icon's representing Tags
 * are clicked on in the Map.  This gets fired off to the any handler.
 */
export interface TagClickedEvent {
  tag: TagInstance;
  event: createjs.MouseEvent;
}
